/**
 * Touch detail's own shell: heading, filters, the Ledger's count line, export, table,
 * load-more.
 *
 * Forked from DetailPage (shared, append-only) as Claim detail's was, because the count
 * line is the page's own sentence — "N touches of M on file" — not a generic row count;
 * and sorting runs over every filtered touch, not just the page on screen.
 *
 * DataTable sorts the rows it is handed, which here is one page of 500. So the shell
 * owns the sort, as ClaimDetailShell does: it reads header clicks, sorts the whole
 * filtered set, and hands the table a page already in order, remounting it with the
 * same key and direction so the header shows the right arrow.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { DataTable, type Column } from './Table'
import { useUrlState, sortSpec } from '@/ui/urlState'
import { PopOut } from './PopOut'
import { copyRows, downloadCSV, type ExportColumn, type ExportResult } from '@/ui/export'
import { int } from '@/format'

/** Her first page, then her step: 200 rows, then 500 more per click. */
const FIRST = 200
const PAGE = 500

export function TouchDetailShell<T>({
  title,
  grain,
  filters,
  summary,
  rows,
  columns,
  exportColumns,
  exportName,
  rowKey,
  onRowClick,
  empty,
}: {
  title: string
  grain: string
  filters: ReactNode
  /** The count line above the table. */
  summary: ReactNode
  rows: T[]
  columns: Column<T>[]
  exportColumns: ExportColumn<T>[]
  exportName: string
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  /** Shown instead of the table when there are no rows at all. */
  empty: ReactNode
}) {
  const [shown, setShown] = useState(FIRST)
  const [flash, setFlash] = useState<string | null>(null)

  // In the URL (`?sort=col.dir`), so Back from a drill and a copied link keep the order.
  const [sort, setSort] = useUrlState('sort', null, sortSpec)

  const sorted = useMemo(() => {
    const get = columns.find((c) => c.key === sort?.key)?.sort
    if (!sort || !get) return rows
    // Keys computed once per row, not once per comparison.
    const keyed = rows.map((r) => ({ k: get(r), r }))
    const sign = sort.dir === 'asc' ? 1 : -1
    keyed.sort((a, b) => {
      // Nulls and blanks last in both directions, as hers does.
      const an = a.k === null || a.k === ''
      const bn = b.k === null || b.k === ''
      if (an) return bn ? 0 : 1
      if (bn) return -1
      const c =
        typeof a.k === 'number' && typeof b.k === 'number'
          ? a.k - b.k
          : String(a.k).localeCompare(String(b.k))
      return sign * c
    })
    return keyed.map((x) => x.r)
  }, [rows, columns, sort])

  // A new filter or sort starts again from the top page, as hers does.
  useEffect(() => setShown(FIRST), [rows, sort])

  const visible = useMemo(() => sorted.slice(0, shown), [sorted, shown])
  const more = sorted.length - visible.length

  /** Header clicks, read before DataTable's own handler; same toggle rules as it. */
  const onHeaderClick = (e: React.MouseEvent) => {
    // A column resize ends in a click on its handle, which is not a sort.
    if ((e.target as HTMLElement).closest('.dt-resize')) return
    const th = (e.target as HTMLElement).closest('th')
    if (!th || !th.parentElement) return
    const c = columns[Array.prototype.indexOf.call(th.parentElement.children, th)]
    if (!c?.sort) return
    setSort((s) =>
      s?.key === c.key
        ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: c.key, dir: c.align === 'right' ? 'desc' : 'asc' },
    )
  }

  const say = (msg: string) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 3000)
  }

  const onCopy = async () => {
    const r = await copyRows(sorted, exportColumns)
    say(
      r === 'ok'
        ? `Copied ${int(sorted.length)} rows — paste straight into a sheet`
        : 'Copy was blocked by the browser',
    )
  }

  const onCsv = () => {
    const r: ExportResult = downloadCSV(exportName, sorted, exportColumns)
    say(
      r === 'ok'
        ? `Saved ${exportName}`
        : 'This view cannot start a download — use Copy instead',
    )
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="page-title">{title}</h1>
        <span className="pill">{grain}</span>
      </div>
      <PopOut title={title} note={grain}>
        {({ button }) => (
          <>
            <div style={{ marginTop: 16 }}>{filters}</div>

            <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '14px 0 0' }}>{summary}</p>

            {rows.length === 0 ? (
              <div className="card" style={{ marginTop: 10, borderLeft: '3px solid var(--accent)' }}>
                {empty}
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {flash && (
                      <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-2)' }}>
                        {flash}
                      </span>
                    )}
                    {button}
                    <span className="eyebrow">Export</span>
                    <button
                      type="button"
                      onClick={() => void onCopy()}
                      title="Copy as tab-separated text, ready to paste into a spreadsheet"
                      style={btn}
                    >
                      Copy
                    </button>
                    <button type="button" onClick={onCsv} title="Save as a CSV file" style={btn}>
                      CSV
                    </button>
                  </span>
                </div>

                <div className="popout-fill">
                  <div
                    className="card"
                    style={{ marginTop: 10, padding: 0, overflow: 'hidden' }}
                    onClickCapture={onHeaderClick}
                  >
                    <DataTable
                      storageKey="touch-detail.rows"
                      key={sort ? `${sort.key}:${sort.dir}` : 'unsorted'}
                      initialSort={sort?.key}
                      initialDir={sort?.dir}
                      rows={visible}
                      columns={columns}
                      rowKey={rowKey}
                      onRowClick={onRowClick}
                      maxHeight="72vh"
                    />
                  </div>
                </div>

                {more > 0 && (
                  <button
                    type="button"
                    className="reset-btn"
                    onClick={() => setShown((n) => n + PAGE)}
                    style={{ marginTop: 10, alignSelf: 'flex-start' }}
                  >
                    Show {int(Math.min(PAGE, more))} more of {int(more)} remaining
                  </button>
                )}
              </>
            )}
          </>
        )}
      </PopOut>
    </>
  )
}

const btn: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  cursor: 'pointer',
}
